import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  Card,
  Spinner,
  Button,
  Table,
  Badge,
  TextInput,
  Select,
  Modal,
  Dropdown,
} from "flowbite-react";
import { Store } from "../context/store";
import apiRequest from "../utils/apiRequest";
import {
  HiOutlineDocumentAdd,
  HiSearch,
  HiPlus,
  HiPencilAlt,
  HiTrash,
  HiCheck,
  HiDotsVertical,
  HiCash,
  HiTrendingUp,
  HiDocumentText,
  HiEye, // 🚨 New icon for View Payments
} from "react-icons/hi";
import { format } from "date-fns";
import { estates } from "../assets/data/estates";
import { useAllPermissions } from "../utils/usePermission";
import { confirmDialog } from "../components/ConfirmDialog";
import DashboardLayout from "../components/layout/DashboardLayout";

// Define the available categories from the schema
const CASHFLOW_CATEGORIES = [
  "contractor",
  "joint-venture-partner",
  "supplier",
  "joint-investor-agent",
  "investors",
  "trade-creditors",
  "trade-debitors",
  "professional-services",
  "mortgage-clients",
  "agency",
  "other",
];

// Helper to format currency
const formatCurrency = (amount) => {
  if (typeof amount !== "number") return "₦0.00";
  return `₦${amount.toLocaleString("en-NG", { minimumFractionDigits: 0 })}`;
};

// Helper to get status color
const getStatusBadge = (balance) => {
  if (balance <= 0) {
    return (
      <Badge color="success" icon={HiCheck}>
        PAID
      </Badge>
    );
  }
  return (
    <Badge color="warning" icon={HiCash}>
      OUTSTANDING
    </Badge>
  );
};

function Cashflow() {
  let store = useContext(Store);
  let [baseUrl] = store.url;
  let [currentUser] = store.loggedInUser;

  // --- State Management ---
  const [allCashflows, setAllCashflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [deletingCashflowId, setDeletingCashflowId] = useState(null);
  const [deletingPaymentId, setDeletingPaymentId] = useState(null);
  const [searchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const [openModal, setOpenModal] = useState(null); // 'create', 'edit', 'payment', 'variation'
  // 🚨 NEW STATE: For the new payments modal
  const [openPaymentDetailsModal, setOpenPaymentDetailsModal] = useState(false);
  const [currentCashflow, setCurrentCashflow] = useState(null);
  const [formData, setFormData] = useState({});
  const [clientSearch, setClientSearch] = useState("");
  const [purposeSearch, setPurposeSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [transactionType, setTransactionType] = useState("outflow");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { canWriteCashflow, canDeleteCashflow } = useAllPermissions();
  const isActionLoading = Boolean(actionLoading);
  const isDeletingCashflow = Boolean(deletingCashflowId);
  const currentUserRoleTitle = currentUser?.role?.title
    ?.toLowerCase()
    .replace(/[-_\s]+/g, " ")
    .trim();
  const isAdminOrSuperAdmin =
    currentUserRoleTitle === "admin" ||
    currentUserRoleTitle === "super admin";

  // --- Core API Handlers ---

  const fetchAllCashflows = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("royal-token");

      const params = new URLSearchParams();

      if (clientSearch) params.append("clientName", clientSearch);
      if (purposeSearch) params.append("purpose", purposeSearch);
      if (selectedCategory) params.append("category", selectedCategory);
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);

      // ✅ default outflow will always be sent unless user changes it
      if (transactionType) params.append("transactionType", transactionType);

      const url = `${baseUrl}/cashflows?${params.toString()}`;

      const data = await apiRequest(url, "GET", null, token);
      setAllCashflows(data.data);
    } catch (err) {
      console.error("Failed to fetch cashflows:", err);
    } finally {
      setLoading(false);
    }
  }, [
    baseUrl,
    clientSearch,
    purposeSearch,
    selectedCategory,
    startDate,
    endDate,
    transactionType, // 👈 important
  ]);

  // --- Local Filtering and Aggregation ---

  const filteredCashflows = useMemo(() => {
    let list = allCashflows;
    const lowerCaseSearch = searchQuery.toLowerCase();
    const categoryFilter = selectedCategory;

    if (categoryFilter) {
      list = list.filter((flow) => flow.category === categoryFilter);
    }

    if (lowerCaseSearch) {
      list = list.filter(
        (flow) =>
          flow.clientName.toLowerCase().includes(lowerCaseSearch) ||
          flow.purpose.toLowerCase().includes(lowerCaseSearch),
      );
    }

    return list;
  }, [allCashflows, searchQuery, selectedCategory]);

  const getFilteredAnalytics = (flows) => {
    return flows.reduce(
      (acc, flow) => {
        acc.totalAgreed += flow.totalAgreedAmount || 0;
        acc.totalPaid += flow.totalAmountPaid || 0;
        acc.totalBalance += flow.balanceLeft || 0;
        acc.totalRecords += 1;
        return acc;
      },
      {
        totalAgreed: 0,
        totalPaid: 0,
        totalBalance: 0,
        totalRecords: 0,
      },
    );
  };

  const analytics = useMemo(
    () => getFilteredAnalytics(filteredCashflows),
    [filteredCashflows],
  );

  // --- CRUD Handlers (Unchanged, removed comments for brevity) ---

  const handleCreateCashflow = async () => {
    if (
      !formData.clientName ||
      !formData.purpose ||
      !formData.initialAgreedAmount ||
      !formData.category
    ) {
      alert("Please fill all required fields.");
      return;
    }

    let purpose = formData.purpose;

    if (formData.category === "contractor" && formData.siteName) {
      purpose = `${formData.purpose.toUpperCase()} - [${formData.siteName.toUpperCase()}]`;
    }

    try {
      setActionLoading("create");
      const token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/cashflow`;
      const payload = {
        ...formData,
        purpose: purpose,
        createdBy: currentUser.id,
        initialAgreedAmount: Number(formData.initialAgreedAmount),
        transactionType: formData.transactionType || "outflow",
      };

      await apiRequest(url, "POST", payload, token);
      alert("Cashflow record created successfully!");
      closeModal();
      await fetchAllCashflows();
    } catch (error) {
      alert(`Error creating cashflow: ${error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateCashflow = async () => {
    try {
      setActionLoading("edit");
      const token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/cashflow/${currentCashflow.id}`;
      const payload = {
        ...formData,
        transactionType: formData.transactionType || "outflow",
      };
      await apiRequest(url, "PUT", payload, token);
      alert("Cashflow updated successfully!");
      closeModal();
      await fetchAllCashflows();
    } catch (error) {
      alert(`Error updating cashflow: ${error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteCashflow = async (id) => {
    await confirmDialog(
      "Are you sure you want to delete this cashflow record?",
      {
        title: "Delete Cashflow",
        confirmText: "Yes, delete",
        color: "failure",
        loadingText: "Deleting...",
        onConfirm: async () => {
          try {
            setDeletingCashflowId(id);
            const token = localStorage.getItem("royal-token");
            const url = `${baseUrl}/cashflow/${id}`;
            await apiRequest(url, "DELETE", null, token);
            alert("Cashflow deleted successfully!");
            await fetchAllCashflows();
          } catch (error) {
            alert(`Error deleting cashflow: ${error.message}`);
          } finally {
            setDeletingCashflowId(null);
          }
        },
      },
    );
  };

  const handleDeletePayment = async (payment) => {
    const recordId = currentCashflow?.id || currentCashflow?._id;
    const paymentId = payment?.id || payment?._id;

    if (!recordId || !paymentId) {
      alert("Unable to identify this payment record.");
      return;
    }

    await confirmDialog(
      "Are you sure you want to delete this payment record?",
      {
        title: "Delete Payment",
        confirmText: "Yes, delete",
        color: "failure",
        loadingText: "Deleting...",
        onConfirm: async () => {
          setDeletingPaymentId(paymentId);
          try {
            const token = localStorage.getItem("royal-token");
            const response = await fetch(
              `${baseUrl}/payment/cashflow/${recordId}/${paymentId}`,
              {
                method: "DELETE",
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            );

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              throw new Error(errorData.message || "Failed to delete payment.");
            }

            const result = await response.json().catch(() => null);
            const updatedCashflow = result?.data;

            if (updatedCashflow) {
              setCurrentCashflow(updatedCashflow);
              setAllCashflows((prev) =>
                prev.map((cashflow) =>
                  cashflow.id === updatedCashflow.id ||
                  cashflow._id === updatedCashflow._id
                    ? updatedCashflow
                    : cashflow,
                ),
              );
            } else {
              const filteredPayments =
                currentCashflow?.payments?.filter(
                  (item) => (item.id || item._id) !== paymentId,
                ) || [];

              setCurrentCashflow((prev) => ({
                ...prev,
                payments: filteredPayments,
                totalAmountPaid: Math.max(
                  Number(prev?.totalAmountPaid || 0) -
                    Number(payment.amountPaid || 0),
                  0,
                ),
                balanceLeft:
                  Number(prev?.balanceLeft || 0) +
                  Number(payment.amountPaid || 0),
              }));

              await fetchAllCashflows();
            }
          } catch (error) {
            alert(error.message || "Error deleting payment.");
          } finally {
            setDeletingPaymentId(null);
          }
        },
      },
    );
  };

  const handleAddPayment = async () => {
    if (!formData.amountPaid) {
      alert("Payment amount is required.");
      return;
    }
    try {
      setActionLoading("payment");
      const token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/payment/cashflow/${currentCashflow.id}`;
      const payload = {
        amountPaid: Number(formData.amountPaid),
        proof: formData.proof,
      };
      await apiRequest(url, "PUT", payload, token);
      alert("Payment successfully added!");
      closeModal();
      await fetchAllCashflows();
    } catch (error) {
      alert(`Error adding payment: ${error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateVariation = async () => {
    if (!formData.revisedAgreedAmount) {
      alert("Revised agreed amount is required.");
      return;
    }
    try {
      setActionLoading("variation");
      const token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/variation/cashflow/${currentCashflow.id}`;
      const payload = {
        revisedAgreedAmount: Number(formData.revisedAgreedAmount),
        variationNote: formData.variationNote,
      };
      await apiRequest(url, "PUT", payload, token);
      alert("Cashflow variation updated!");
      closeModal();
      await fetchAllCashflows();
    } catch (error) {
      alert(`Error updating variation: ${error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  // --- Effects & Utility Handlers ---

  useEffect(() => {
    fetchAllCashflows();
  }, [fetchAllCashflows]);

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const openCreateModal = () => {
    setFormData({ transactionType: "outflow" });
    setOpenModal("create");
  };

  const openEditModal = (cashflow) => {
    setCurrentCashflow(cashflow);
    setFormData({
      clientName: cashflow.clientName,
      phone: cashflow.phone,
      purpose: cashflow.purpose,
      initialAgreedAmount: cashflow.initialAgreedAmount,
      category: cashflow.category,
      message: cashflow.message,
      transactionType: cashflow.transactionType || "outflow",
    });
    setOpenModal("edit");
  };

  const openAddPaymentModal = (cashflow) => {
    setCurrentCashflow(cashflow);
    setFormData({ amountPaid: "", proof: "" });
    setOpenModal("payment");
  };

  const openVariationModal = (cashflow) => {
    setCurrentCashflow(cashflow);
    setFormData({
      revisedAgreedAmount:
        cashflow.revisedAgreedAmount || cashflow.totalAgreedAmount,
      variationNote: cashflow.variationNote || "",
    });
    setOpenModal("variation");
  };

  // 🚨 NEW HANDLER: Opens the Payment Details modal
  const openPaymentDetails = (cashflow) => {
    setCurrentCashflow(cashflow);
    setOpenPaymentDetailsModal(true);
  };

  const closeModal = () => {
    if (isActionLoading) return;
    setOpenModal(null);
    setOpenPaymentDetailsModal(false); // Close payment details modal too
    setCurrentCashflow(null);
    setFormData({});
  };

  // --- Render Content ---

  // KICKER FOR ANALYTICS CARD (Unchanged)
  const AnalyticsCard = ({
    title,
    value,
    colorClass,
    icon: Icon,
    notCurrency,
  }) => (
    <Card
      className="shadow-lg p-2 dark:bg-gray-800 border-l-4"
      style={{ borderColor: colorClass }}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">
            {title}
          </p>
          <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white mt-1">
            {notCurrency ? value : formatCurrency(value)}
          </h2>
        </div>
        <Icon className={`w-8 h-8 ${colorClass} opacity-60`} />
      </div>
    </Card>
  );

  // 🚨 NEW MODAL COMPONENT
  const PaymentDetailsModal = () => {
    const payments = currentCashflow?.payments || [];

    return (
      <Modal
        show={openPaymentDetailsModal}
        onClose={closeModal}
        popup
        size="4xl" // Wider modal for the table
        theme={{
          root: {
            base: "fixed inset-0 z-[2000] h-full w-full overflow-y-auto overflow-x-hidden md:inset-0 md:h-full",
          },
          content: {
            inner:
              "relative rounded-lg bg-white shadow dark:bg-gray-700 flex flex-col max-h-full",
          },
          backdrop: "fixed inset-0 z-[1990] bg-gray-900/50 dark:bg-gray-900/80",
        }}
      >
        <Modal.Header>
          Payment History for: {currentCashflow?.clientName}
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <p className="text-sm font-semibold">
              Total Paid:{" "}
              <span className="text-green-600">
                {formatCurrency(currentCashflow?.totalAmountPaid)}
              </span>
            </p>

            {payments.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                No payments recorded yet for this cashflow.
              </p>
            ) : (
              <Table hoverable>
                <Table.Head>
                  <Table.HeadCell>S/No.</Table.HeadCell>
                  <Table.HeadCell>AMOUNT PAID</Table.HeadCell>
                  <Table.HeadCell>DATE PAID</Table.HeadCell>
                  <Table.HeadCell>PROOF</Table.HeadCell>
                  <Table.HeadCell>ADDED BY</Table.HeadCell>
                  <Table.HeadCell>
                    <span className="sr-only">Actions</span>
                  </Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {payments.map((payment, index) => {
                    const paymentId = payment.id || payment._id;

                    return (
                    <Table.Row
                      key={paymentId || index}
                      className="bg-white dark:border-gray-700 dark:bg-gray-800"
                    >
                      <Table.Cell>{index + 1}</Table.Cell>
                      <Table.Cell className="font-semibold text-green-600">
                        {formatCurrency(payment.amountPaid)}
                      </Table.Cell>
                      <Table.Cell>
                        {format(new Date(payment.datePaid), "MMM d, yyyy @ p")}
                      </Table.Cell>
                      <Table.Cell>
                        {payment.proof ? (
                          // <a
                          //   href={payment.proof}
                          //   target="_blank"
                          //   rel="noopener noreferrer"
                          //   className="text-cyan-600 hover:underline flex items-center"
                          // >
                          //   View Proof{" "}
                          //   <HiExternalLink className="w-4 h-4 ml-1" />
                          // </a>
                          <span className="text-cyan-600 flex items-center">
                            {payment.proof}
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </Table.Cell>
                      <Table.Cell className="text-sm">
                        {/* Displays the ObjectId of the staff. If staff details are populated, you could display the name/email instead. */}
                        {payment.addedBy
                          ? `${payment.addedBy.firstName} ${payment.addedBy.lastName}`
                          : "N/A"}
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          size="xs"
                          color="failure"
                          onClick={() => handleDeletePayment(payment)}
                          disabled={
                            !isAdminOrSuperAdmin ||
                            !canDeleteCashflow ||
                            // !paymentId ||
                            deletingPaymentId === paymentId
                          }
                        >
                          {deletingPaymentId === paymentId ? (
                            <Spinner size="sm" />
                          ) : (
                            <>
                              <HiTrash className="w-4 h-4 mr-1" />
                              Delete
                            </>
                          )}
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            )}

            <div className="flex justify-end">
              <Button onClick={closeModal} color="light">
                Close
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <>
      <title>Vendor Management</title>
      <DashboardLayout active="cashflow" contentClassName="lg:p-8">
            <h5 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-6 flex items-center">
              <HiCash className="w-8 h-8 mr-3 text-green-600" />
              Record Management
            </h5>

            <hr className="my-6 border-gray-200 dark:border-gray-700" />

            {/* --- 1. Analytics Summary (Local Aggregate) --- */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* ... (AnalyticsCard components) ... */}
              <AnalyticsCard
                title="Total Agreed (Filtered)"
                value={analytics.totalAgreed}
                colorClass="text-indigo-500 border-indigo-500"
                icon={HiOutlineDocumentAdd}
              />
              <AnalyticsCard
                title="Total Paid (Filtered)"
                value={analytics.totalPaid}
                colorClass="text-green-500 border-green-500"
                icon={HiCheck}
              />
              <AnalyticsCard
                title="Total Balance (Filtered)"
                value={analytics.totalBalance}
                colorClass="text-red-500 border-red-500"
                icon={HiTrendingUp}
              />
              <AnalyticsCard
                title="Total Records (Filtered)"
                value={analytics.totalRecords}
                colorClass="text-cyan-500 border-cyan-500"
                icon={HiDocumentText}
                notCurrency={true}
              />
            </div>

            {/* --- 2. Controls and Filters (Unchanged) --- */}

            <div className="mb-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    color="light"
                    onClick={() => setFiltersOpen((prev) => !prev)}
                    className="w-full sm:w-auto"
                  >
                    {filtersOpen ? "Hide Filters" : "Show Filters"}
                  </Button>
                </div>

                <Button
                  disabled={
                    !canWriteCashflow || isActionLoading || isDeletingCashflow
                  }
                  onClick={openCreateModal}
                  color="indigo"
                  className="w-full sm:w-auto"
                >
                  <HiPlus className="w-5 h-5 mr-2" />
                  New Record
                </Button>
              </div>

              {filtersOpen && (
                <div className="mt-4 bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-5 shadow-sm">
                  <div className="flex flex-col gap-1">
                    <h6 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Filters
                    </h6>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Narrow down records by client, purpose, date range,
                      category, and transaction type.
                    </p>
                  </div>

                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
                    <div className="lg:col-span-2">
                      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                        Client Name
                      </label>
                      <TextInput
                        icon={HiSearch}
                        placeholder="Search by client name"
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                      />
                    </div>

                    <div className="lg:col-span-2">
                      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                        Purpose / Project
                      </label>
                      <TextInput
                        icon={HiSearch}
                        placeholder="Search by project title, site or purpose"
                        value={purposeSearch}
                        onChange={(e) => setPurposeSearch(e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                        Start Date
                      </label>
                      <TextInput
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                        End Date
                      </label>
                      <TextInput
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                        Category
                      </label>
                      <Select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                      >
                        <option value="">All Categories</option>
                        {CASHFLOW_CATEGORIES.map((cat) => (
                          <option key={cat} value={cat} className="capitalize">
                            {cat.replace(/-/g, " ")}
                          </option>
                        ))}
                      </Select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                        Transaction Type
                      </label>
                      <Select
                        value={transactionType}
                        onChange={(e) => setTransactionType(e.target.value)}
                      >
                        <option value="outflow">Outflow</option>
                        <option value="inflow">Inflow</option>
                      </Select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* --- 3. Cashflow List Table --- */}
            <Card className="shadow-2xl dark:bg-gray-800 overflow-x-auto min-h-[400px]">
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <Spinner size="xl" color="info" />
                </div>
              ) : filteredCashflows.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                  No cashflow records found. Try adjusting your filters.
                </p>
              ) : (
                <Table
                  hoverable
                  className="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                >
                  <Table.Head>
                    <Table.HeadCell>VENDOR/CLIENT NAME</Table.HeadCell>
                    <Table.HeadCell>PURPOSE / CATEGORY</Table.HeadCell>
                    <Table.HeadCell>TOTAL AGREED</Table.HeadCell>
                    <Table.HeadCell>PAID</Table.HeadCell>
                    <Table.HeadCell>BALANCE</Table.HeadCell>
                    <Table.HeadCell>STATUS</Table.HeadCell>
                    <Table.HeadCell>CREATED</Table.HeadCell>
                    <Table.HeadCell>ACTIONS</Table.HeadCell>
                  </Table.Head>
                  <Table.Body className="divide-y">
                    {filteredCashflows.map((flow) => (
                      <Table.Row
                        key={flow.id}
                        className="bg-white dark:border-gray-700 dark:bg-gray-800 uppercase"
                      >
                        <Table.Cell className="font-medium text-gray-900 dark:text-white">
                          {flow.clientName}
                        </Table.Cell>
                        <Table.Cell>
                          <p className="font-semibold">{flow.purpose}</p>
                          <Badge
                            color="gray"
                            size="sm"
                            className="mt-1 capitalize"
                          >
                            {flow.category.replace(/-/g, " ")}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell className="font-semibold text-indigo-600">
                          {formatCurrency(flow.totalAgreedAmount)}
                        </Table.Cell>
                        <Table.Cell className="font-semibold text-green-600">
                          {formatCurrency(flow.totalAmountPaid)}
                        </Table.Cell>
                        <Table.Cell className="font-semibold text-red-600">
                          {formatCurrency(flow.balanceLeft)}
                        </Table.Cell>
                        <Table.Cell>
                          {getStatusBadge(flow.balanceLeft)}
                        </Table.Cell>
                        <Table.Cell>
                          {format(new Date(flow.createdAt), "MMM d, yyyy")}
                        </Table.Cell>
                        <Table.Cell>
                          <Dropdown
                            label={<HiDotsVertical className="w-5 h-5" />}
                            inline
                            arrowIcon={false}
                          >
                            {/* 🚨 NEW: View Payments Dropdown Item */}
                            <Dropdown.Item
                              onClick={() => openPaymentDetails(flow)}
                              icon={HiEye}
                            >
                              View Payments ({flow.payments.length})
                            </Dropdown.Item>

                            <Dropdown.Item
                              onClick={() => openEditModal(flow)}
                              icon={HiPencilAlt}
                              disabled={
                                !canWriteCashflow ||
                                isActionLoading ||
                                isDeletingCashflow
                              }
                              style={{
                                background: canWriteCashflow ? "" : "#d1d1d2",
                                cursor: canWriteCashflow
                                  ? "pointer"
                                  : "not-allowed",
                              }}
                            >
                              Edit Record
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => openAddPaymentModal(flow)}
                              icon={HiCash}
                              disabled={
                                +flow.balanceLeft <= 0 ||
                                !canWriteCashflow ||
                                isActionLoading ||
                                isDeletingCashflow
                              }
                              style={{
                                background: canWriteCashflow ? "" : "#d1d1d2",
                                cursor: canWriteCashflow
                                  ? "pointer"
                                  : "not-allowed",
                              }}
                            >
                              Add Payment
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => openVariationModal(flow)}
                              icon={HiTrendingUp}
                              disabled={
                                +flow.balanceLeft <= 0 ||
                                !canWriteCashflow ||
                                isActionLoading ||
                                isDeletingCashflow
                              }
                              style={{
                                background: canWriteCashflow ? "" : "#d1d1d2",
                                cursor: canWriteCashflow
                                  ? "pointer"
                                  : "not-allowed",
                              }}
                            >
                              Update Variation
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item
                              onClick={() => handleDeleteCashflow(flow.id)}
                              icon={HiTrash}
                              className="text-red-600"
                              disabled={
                                !canDeleteCashflow ||
                                isActionLoading ||
                                isDeletingCashflow
                              }
                              style={{
                                background: canDeleteCashflow ? "" : "#d1d1d2",
                                cursor: canDeleteCashflow
                                  ? "pointer"
                                  : "not-allowed",
                              }}
                            >
                              {deletingCashflowId === flow.id ? (
                                <span className="inline-flex items-center">
                                  <Spinner size="sm" className="mr-2" />
                                  Deleting...
                                </span>
                              ) : (
                                "Delete Record"
                              )}
                            </Dropdown.Item>
                          </Dropdown>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}
            </Card>
      </DashboardLayout>

      {/* --- MODALS --- */}

      {/* Payment Details Modal (New) */}
      <PaymentDetailsModal />

      {/* Create/Edit Modal (Unchanged) */}
      <Modal
        show={openModal === "create" || openModal === "edit"}
        onClose={closeModal}
        popup
        theme={{
          root: {
            base: "fixed inset-0 z-[2000] h-full w-full overflow-y-auto overflow-x-hidden md:inset-0 md:h-full",
          },
          content: {
            inner:
              "relative rounded-lg bg-white shadow dark:bg-gray-700 flex flex-col max-h-full",
          },
          backdrop: "fixed inset-0 z-[1990] bg-gray-900/50 dark:bg-gray-900/80",
        }}
      >
        <Modal.Header>
          {openModal === "create" ? "New Record" : "Edit Record"}
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <TextInput
              name="clientName"
              placeholder="Vendor/Client Name*"
              value={formData.clientName || ""}
              onChange={handleFormChange}
              required
            />
            <TextInput
              name="phone"
              placeholder="Client Phone"
              value={formData.phone || ""}
              onChange={handleFormChange}
            />
            <Select
              name="category"
              value={formData.category || ""}
              onChange={handleFormChange}
              required
            >
              <option value="">Select Category*</option>
              {CASHFLOW_CATEGORIES.map((cat) => (
                <option key={cat} value={cat} className="capitalize">
                  {cat.replace(/-/g, " ")}
                </option>
              ))}
            </Select>

            <Select
              name="transactionType"
              value={formData.transactionType || "outflow"}
              onChange={handleFormChange}
              required
            >
              <option value="outflow">Outflow</option>
              <option value="inflow">Inflow</option>
            </Select>

            {/* Site Name Select field */}
            {(openModal === "create" || openModal === "edit") &&
              formData.category === "contractor" && (
                <Select
                  name="siteName"
                  value={formData.siteName || ""}
                  onChange={handleFormChange}
                  required={openModal === "create"}
                >
                  <option value="">Select Site Name*</option>

                  {estates.map((estate, index) => {
                    const value = `${estate.name} - ${estate.address}`;
                    return (
                      <option key={index} value={value}>
                        {estate.name} — {estate.address}
                      </option>
                    );
                  })}
                </Select>
              )}

            <TextInput
              name="purpose"
              placeholder="Purpose / Project Title*"
              value={formData.purpose || ""}
              onChange={handleFormChange}
              required
            />
            <TextInput
              name="initialAgreedAmount"
              type="number"
              step="0.01"
              placeholder="Initial Agreed Amount (₦)*"
              value={formData.initialAgreedAmount || ""}
              onChange={handleFormChange}
              required
            />

            <TextInput
              name="message"
              placeholder="Notes/Message"
              value={formData.message || ""}
              onChange={handleFormChange}
            />
            <Button
              onClick={
                openModal === "create"
                  ? handleCreateCashflow
                  : handleUpdateCashflow
              }
              disabled={isActionLoading}
              color="indigo"
              className="w-full"
            >
              {isActionLoading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  {openModal === "create" ? "Creating..." : "Saving..."}
                </>
              ) : openModal === "create" ? (
                "Create Record"
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Add Payment Modal (Unchanged) */}
      <Modal
        show={openModal === "payment"}
        onClose={closeModal}
        popup
        theme={{
          root: {
            base: "fixed inset-0 z-[2000] h-full w-full overflow-y-auto overflow-x-hidden md:inset-0 md:h-full",
          },
          content: {
            inner:
              "relative rounded-lg bg-white shadow dark:bg-gray-700 flex flex-col max-h-full",
          },
          backdrop: "fixed inset-0 z-[1990] bg-gray-900/50 dark:bg-gray-900/80",
        }}
      >
        <Modal.Header>
          Add Payment to {currentCashflow?.clientName}
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <p className="text-sm">
              Balance Left:{" "}
              <span className="font-bold text-red-500">
                {formatCurrency(currentCashflow?.balanceLeft)}
              </span>
            </p>
            <TextInput
              name="amountPaid"
              type="number"
              step="0.01"
              placeholder="Amount Paid (₦)*"
              value={formData.amountPaid || ""}
              onChange={handleFormChange}
              required
            />
            <TextInput
              name="proof"
              placeholder="Payment Proof URL (Optional)"
              value={formData.proof || ""}
              onChange={handleFormChange}
            />
            <Button
              onClick={handleAddPayment}
              disabled={isActionLoading}
              color="success"
              className="w-full"
            >
              {actionLoading === "payment" ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Recording...
                </>
              ) : (
                <>
                  <HiCash className="w-5 h-5 mr-2" /> Record Payment
                </>
              )}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Update Variation Modal (Unchanged) */}
      <Modal
        show={openModal === "variation"}
        onClose={closeModal}
        popup
        theme={{
          root: {
            base: "fixed inset-0 z-[2000] h-full w-full overflow-y-auto overflow-x-hidden md:inset-0 md:h-full",
          },
          content: {
            inner:
              "relative rounded-lg bg-white shadow dark:bg-gray-700 flex flex-col max-h-full",
          },
          backdrop: "fixed inset-0 z-[1990] bg-gray-900/50 dark:bg-gray-900/80",
        }}
      >
        <Modal.Header>
          Update Variation for {currentCashflow?.clientName}
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <p className="text-sm">
              Initial Agreed:{" "}
              <span className="font-bold">
                {formatCurrency(currentCashflow?.initialAgreedAmount)}
              </span>
            </p>
            <TextInput
              name="revisedAgreedAmount"
              type="number"
              step="0.01"
              placeholder="Revised Agreed Amount (₦)*"
              value={formData.revisedAgreedAmount || ""}
              onChange={handleFormChange}
              required
            />
            <TextInput
              name="variationNote"
              placeholder="Variation Note/Reason"
              value={formData.variationNote || ""}
              onChange={handleFormChange}
            />
            <Button
              onClick={handleUpdateVariation}
              disabled={isActionLoading}
              color="warning"
              className="w-full"
            >
              {actionLoading === "variation" ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Applying...
                </>
              ) : (
                <>
                  <HiTrendingUp className="w-5 h-5 mr-2" /> Apply Variation
                </>
              )}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default Cashflow;
